import { ClientStore, StoreRelations } from 'src/app/stores/stores';
import { CurrencyPipe } from '@angular/common';

export default class StoreUtils {
  static isAsapOrderEnabled(store: ClientStore, deliveryMode: string): boolean {
    // If no order receive option is selected, then do not show the component
    if (deliveryMode === undefined) {
      return false;
    }
    // If delivery at table is selected, then show the component only if selected in admin section
    if (
      deliveryMode === 'IN_STORE_LOCATION' &&
      store.settings.DELIVERY_IN_STORE_LOCATION_CHOOSE_ASAP_ORDER_DATE
    ) {
      return true;
    }
    // If pickup is selected, then show the component if selected in admin section
    if (
      deliveryMode === 'NO_LOCATION' &&
      store.settings.DELIVERY_NO_LOCATION_CHOOSE_ASAP_ORDER_DATE
    ) {
      return true;
    }
    // If delivery at address is selected, then show the component if selected in admin section
    if (
      deliveryMode === 'ADDRESS' &&
      store.settings.DELIVERY_ADDRESS_CHOOSE_ASAP_ORDER_DATE
    ) {
      return true;
    }

    return false;
  }

  static isFutureOrderEnabled(
    store: ClientStore,
    deliveryMode: string,
  ): boolean {
    // If no order receive option is selected, then do not show the component
    if (deliveryMode === undefined) {
      return false;
    }
    // If delivery at table is selected, then show the component only if selected in admin section
    if (
      deliveryMode === 'IN_STORE_LOCATION' &&
      store.settings.DELIVERY_IN_STORE_LOCATION_CHOOSE_FUTURE_ORDER_DATE
    ) {
      return true;
    }
    // If pickup is selected, then show the component if selected in admin section
    if (
      deliveryMode === 'NO_LOCATION' &&
      store.settings.DELIVERY_NO_LOCATION_CHOOSE_FUTURE_ORDER_DATE
    ) {
      return true;
    }
    // If delivery at address is selected, then show the component if selected in admin section
    if (
      deliveryMode === 'ADDRESS' &&
      store.settings.DELIVERY_ADDRESS_CHOOSE_FUTURE_ORDER_DATE
    ) {
      return true;
    }

    return false;
  }

  static getCurrencySymbol(store: ClientStore) {
    if (store.selectedCurrency) {
      return store.selectedCurrency.isoCode === 'XCD'
        ? 'XCD'
        : store.selectedCurrency.symbol;
    }

    if (store.currency.isoCode === 'XCD') {
      return 'XCD';
    }

    return store.currency.symbol;
  }

  static getCurrencySymbolFromCode(currencyCode: string): string {
    if (!currencyCode) return '';
    const currencyPipe = new CurrencyPipe('en-US');
    const formatted = currencyPipe.transform(
      0,
      currencyCode,
      'symbol',
      '1.0-0',
    );
    return formatted ? formatted.replace(/[\d\s.,]/g, '').trim() : currencyCode;
  }

  static getCurrencyIsoCode(store: ClientStore) {
    return store.selectedCurrency
      ? store.selectedCurrency.isoCode
      : store.currency.isoCode;
  }

  static getStoreLocale(store: ClientStore) {
    return store.selectedCurrency
      ? store.selectedCurrency.locale
      : store.locale;
  }

  static getParentStoreId(store: ClientStore) {
    return store?.relation?.parentStore?.storeId;
  }

  /**
   * The parent's descendants that are still part of the group — every row of
   * `relation.allDescendantStores` except the branches the merchant marked
   * Independent (story #11576, a regression of #7347). Marking a branch
   * Independent means "this one runs its own shop, keep it out of the group",
   * so it must not be offered as a pickup point, must not be a candidate for a
   * typed delivery address, and must not count towards which delivery methods
   * the parent offers.
   *
   * The backend is the authoritative filter — `StoreRelationEntityToDtoMapper`
   * drops these rows from the `STOREFRONT` view — so in production this is a
   * no-op. It exists because the rule was previously only stated in the
   * payload: when the backend filter was narrowed to soft-delete only for
   * every view, the seven storefront consumers of the list had nothing of
   * their own to fall back on and the branches reappeared in the chooser.
   * Stated here, one definition serves every consumer.
   *
   * **Per-subtree, not per-row**, which is the whole difficulty. The backend
   * hangs its filter on a *recursing* stream, so an Independent branch prunes
   * everything under it. `allDescendantStores` is the flattened result of that
   * walk, so testing each row's own `isIndependent` is **not** the same rule: a
   * linked grandchild under an Independent branch carries
   * `isIndependent: false` and would survive, and it must not — reaching it
   * puts the customer inside a branch that opted out of the group (TC6). The
   * ancestry walk below reproduces the backend's prune on the flat list, using
   * `parentStoreId`.
   *
   * **Where `parentStoreId` comes from**, since the whole walk rests on it:
   * `StoreRelationEntityToDtoMapper.getAllDescendantStores()` sets it on every
   * row it emits (`mappedChild.setParentStoreId(storeId)`), unconditionally and
   * outside the view branch, so the `STOREFRONT` projection carries it exactly
   * as `ADMIN` does. It names the *immediate* parent rather than the top of the
   * hierarchy, because the method recurses with
   * `getAllDescendantStores(childStore.getStoreId(), view)` and so rebinds
   * `storeId` one level down on each hop. The field arrived with #10411 and is
   * on `master`, so every currently deployed backend populates it — including
   * the older ones this guard exists for. `StoreRelations.parentStoreId` is
   * documented backend-side as "only populated for entries in
   * `allDescendantStores`", which is the only list this accessor reads.
   *
   * The gap this closes is reachable exactly when this guard is the only thing
   * running: a new frontend against a backend that has not shipped the mapper
   * fix yet still receives the unpruned subtree, which is the release window
   * this accessor exists for.
   */
  static getLinkedDescendantStores(store: ClientStore): StoreRelations[] {
    const descendants = store?.relation?.allDescendantStores ?? [];
    const byStoreId = new Map<number, StoreRelations>(
      descendants.map((descendant) => [descendant.storeId, descendant]),
    );

    // Rows whose ancestry the walk could not follow at all. Collected rather
    // than warned inline so one call produces at most one console line: the
    // consumers of this accessor call it per invocation, some of them from a
    // change-detection path.
    const unresolvable: number[] = [];

    // Walk each row up to the parent store. A row is dropped when it is
    // Independent itself or when any ancestor inside this subtree is. The walk
    // ends in one of three ways, kept apart on purpose — the first two are
    // ordinary, the third means the payload broke its contract:
    //
    //  1. `parentStoreId` is not in the map: that is the parent store the list
    //     was built for. It is not one of its own descendants, so the miss is
    //     the terminating condition and NOT "ancestry unknown" — reading it as
    //     an error would drop every row and empty the chooser.
    //  2. The row has been visited already: a malformed cyclic payload. The
    //     visited set stops it, so a self-referential row is merely wrong
    //     rather than hanging the chooser.
    //  3. `parentStoreId` is absent. The backend always sets it (see above), so
    //     this is a backend older than #10411 and the subtree is invisible to
    //     the walk. The row is still kept — dropping rows out of a payload we
    //     do not understand is the worse failure — but it is reported, because
    //     otherwise this filter silently degrades to the per-row test it exists
    //     to replace and nothing anywhere says so.
    const isReachableThroughLinkedStoresOnly = (
      descendant: StoreRelations,
    ): boolean => {
      const visited = new Set<number>();
      let current: StoreRelations | undefined = descendant;
      while (current && !visited.has(current.storeId)) {
        if (current.isIndependent) {
          return false;
        }
        visited.add(current.storeId);
        if (current.parentStoreId == null) {
          unresolvable.push(current.storeId);
          return true;
        }
        current = byStoreId.get(current.parentStoreId);
      }
      return true;
    };

    const linkedDescendants = descendants.filter(
      isReachableThroughLinkedStoresOnly,
    );

    if (unresolvable.length) {
      console.warn(
        `StoreUtils.getLinkedDescendantStores: descendant store(s) ${unresolvable.join(
          ', ',
        )} carry no parentStoreId, so an Independent branch above them cannot be ` +
          'detected and they may be offered as pickup points they should not be. ' +
          'StoreRelationEntityToDtoMapper.getAllDescendantStores is expected to set it (#10411, #11576).',
      );
    }

    return linkedDescendants;
  }

  static isMapsDisabled() {
    return !(window as any).google?.maps;
  }

  static isItemLevelWishDateEnabled(store: ClientStore): boolean {
    return StoreUtils.isItemLevelWishDate(
      store?.settings?.WISH_DATE_PER_ORDER_ITEM,
    );
  }

  /**
   * The same rule for a raw setting value, so a page holding the value in a form
   * control (which is the live one while the admin clicks) decides it the same
   * way as a page reading the loaded store.
   */
  static isItemLevelWishDate(wishDateLevel: unknown): boolean {
    return wishDateLevel === 'ENABLED';
  }

  /**
   * Whether delivery zone restrictions are on, from a raw setting or form value.
   *
   * `store.settings` is typed `{ [key: string]: any }`, and the value shows up in
   * three forms across the app: the real boolean the API sends
   * (`StoreSetting.DELIVERY_ZONE_RESTRICTION` is declared `Boolean.class`, so
   * `SettingsView.render` runs it through `Boolean.valueOf` before serialising),
   * the empty string a form control is initialised with, and the string a
   * hand-edited or legacy value could carry. A bare `!!` turns the string
   * `'false'` into `true`, which would show the #11533 warning on exactly the
   * stores that must stay silent, so normalise here instead — once, for both
   * pages (AI review on rigasp/stores#9887).
   */
  static isDeliveryZoneRestrictionEnabled(zoneRestriction: unknown): boolean {
    if (typeof zoneRestriction === 'string') {
      return zoneRestriction.trim().toLowerCase() === 'true';
    }
    return zoneRestriction === true;
  }

  /**
   * Delivery zone restrictions do not work together with Desired Date & Time
   * Level = Order item Level: in that mode the delivery address travels on the
   * order items and zone resolution reads only the order-level address, so a
   * customer with more than one saved address cannot submit a delivery order
   * (#11533). Informational only — the admin can still save either setting.
   *
   * The whole predicate lives here because two Admin pages show the hint from
   * opposite directions — Schedules owns the level and reads the restriction off
   * the loaded store, Delivery owns the restriction and reads the level off the
   * loaded store — and the two must never disagree. Each page passes the value
   * it owns live and the other from the store.
   */
  static showsMultidayZoneWarning(
    wishDateLevel: unknown,
    zoneRestriction: unknown,
  ): boolean {
    return (
      StoreUtils.isItemLevelWishDate(wishDateLevel) &&
      StoreUtils.isDeliveryZoneRestrictionEnabled(zoneRestriction)
    );
  }

  /**
   * True when a time selection applies for this store and delivery mode, i.e.
   * when something in the customer flow will actually consume time slots:
   *  - future ordering is enabled for the delivery mode (the customer picks a
   *    day and a slot at checkout), or
   *  - the date is asked upfront on the store page, or
   *  - the store uses a wish date per order item.
   *
   * When this is false the customer is never asked when they want the order,
   * so slots must neither be fetched nor allowed to decide whether the store
   * is open -- that decision belongs to the opening hours alone (card #11222).
   */
  static slotsApply(store: ClientStore, deliveryMode: string): boolean {
    // isFutureOrderEnabled() dereferences store.settings unguarded, and this
    // predicate is reached from template-bound code that runs before the store
    // subscription has emitted.
    if (!store?.settings) {
      return false;
    }
    if (StoreUtils.isItemLevelWishDateEnabled(store)) {
      return true;
    }
    if (store?.settings?.DELIVERY_REQUEST_ORDER_DATE_UPFRONT) {
      return true;
    }
    return StoreUtils.isFutureOrderEnabled(store, deliveryMode);
  }

  /**
   * Whether the store has a PDF brochure to show (story #11626). Three places
   * decide on this — the bottom-bar tab, the fragment that opens the page, and
   * the page itself — and they must never disagree, or a tab would open a page
   * with nothing to render. Removal clears the setting to an empty string
   * rather than dropping the key, so a blank value counts as "no brochure".
   */
  static getBrochureUrl(store: ClientStore): string | null {
    const url = store?.settings?.BROCHURE;
    return typeof url === 'string' && url.trim() ? url : null;
  }

  static hasBrochure(store: ClientStore): boolean {
    return !!StoreUtils.getBrochureUrl(store);
  }
}
